import React, { useId, useMemo, useState } from "react";
import { Box, ButtonBase, Skeleton, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ResponsivePie } from "@nivo/pie";
import { roundedPanelSx } from "../Dashboard/overviewPanel";
import { useStockCurrency } from "../../contexts/StockCurrencyContext";
import { formatNumber } from "../Dashboard/Charts/utils/formatNumber";
import { PORTFOLIO_SERIES_COLORS } from "../Dashboard/Charts/constants/chartColors";
import type {
  InstitutionalOwnership,
  PortfolioDistributionItem,
} from "./hooks/useSuperInvestorHoldings";
import { hapticSelection } from "../../lib/haptics";
import Logo from "../shared/Logo";
import { getSymbolImageUrl } from "../../lib/symbolImage";

interface PortfolioChartDatum extends PortfolioDistributionItem {
  chartId: string;
  color: string;
}
const formatPercentage = (value: number) => `${value.toFixed(1)}%`;
const holdingColumns = {
  xs: "minmax(0, 1fr) 76px",
  sm: "minmax(0, 1fr) 54px 76px",
};
const holdingTransitionSx = {
  "@keyframes portfolio-holding-enter": {
    from: { opacity: 0.65, transform: "translateY(3px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  animation: "portfolio-holding-enter 160ms ease-out",
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

interface PortfolioDistributionChartProps {
  data: PortfolioDistributionItem[];
  totalValue: number;
  holdings?: ReadonlyArray<Pick<InstitutionalOwnership, "symbol">>;
  loading?: boolean;
}
const PortfolioDistributionChart = ({
  data,
  totalValue,
  holdings = [],
  loading = false,
}: PortfolioDistributionChartProps) => {
  const theme = useTheme();
  const titleId = useId();
  const { currencySymbol } = useStockCurrency();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const items = useMemo<PortfolioChartDatum[]>(() => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.info.main,
      theme.palette.warning.main,
      ...PORTFOLIO_SERIES_COLORS,
    ];
    return data
      .filter((item) => Number.isFinite(item.value) && item.value > 0)
      .map((item, index) => ({
        ...item,
        chartId: `${item.id}:${index}`,
        color:
          item.id === "other"
            ? theme.palette.text.disabled
            : colors[index % colors.length],
      }));
  }, [data, theme]);
  const active = items.find(
    (item) => item.chartId === (hoveredId ?? selectedId),
  );
  const activeSymbol = holdings.find(
    ({ symbol }) => symbol === active?.id,
  )?.symbol;
  const activeLogoSrc = activeSymbol
    ? getSymbolImageUrl(activeSymbol)
    : undefined;
  const formatValue = (value: number) =>
    Number.isFinite(value) && value >= 0
      ? `${currencySymbol}${formatNumber(value)}`
      : "—";
  const clearSelection = () => {
    setSelectedId(null);
    setHoveredId(null);
  };
  const selectHolding = (id: string) => {
    hapticSelection();
    setHoveredId(null);
    setSelectedId((current) => (current === id ? null : id));
  };

  return (
    <Box
      component="section"
      aria-labelledby={titleId}
      onKeyDown={(event) => {
        if (event.key === "Escape") clearSelection();
      }}
      sx={roundedPanelSx(theme.palette.mode === "dark")}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
          gap: { xs: 2, md: 3 },
          alignItems: "stretch",
        }}
      >
        <Box sx={{ minWidth: 0 }} onClick={clearSelection}>
          <Typography
            id={titleId}
            component="h2"
            sx={{ fontSize: "1rem", fontWeight: 600, mb: 2 }}
          >
            Portfolio distribution
          </Typography>
          {loading && (
            <Box
              role="status"
              aria-label="Loading portfolio distribution"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 320,
              }}
            >
              <Skeleton variant="circular" width={240} height={240} />
            </Box>
          )}
          {!loading && items.length === 0 && (
            <Box
              role="status"
              sx={{
                py: 6,
                textAlign: "center",
                color: "text.secondary",
                fontSize: "0.875rem",
              }}
            >
              No portfolio data available
            </Box>
          )}
          {!loading && items.length > 0 && (
            <>
              <Box
                role="img"
                aria-label={`Portfolio allocation. Reported value ${formatValue(totalValue)}. Inspect individual holdings in the list.`}
                onMouseLeave={() => setHoveredId(null)}
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: { xs: 260, sm: 320 },
                  aspectRatio: "1",
                  mx: "auto",
                }}
              >
                <Box
                  aria-hidden="true"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    userSelect: "none",
                    touchAction: "pan-y",
                    "& path": { cursor: "pointer" },
                  }}
                >
                  <ResponsivePie<PortfolioChartDatum>
                    data={items}
                    id="chartId"
                    animate={false}
                    margin={{ top: 12, right: 12, bottom: 12, left: 12 }}
                    innerRadius={0.72}
                    padAngle={0.7}
                    cornerRadius={2}
                    activeOuterRadiusOffset={4}
                    activeId={active?.chartId ?? null}
                    onActiveIdChange={(id) => {
                      // Keep the preview stable while crossing the space between slices.
                      if (id !== null) setHoveredId(String(id));
                    }}
                    onClick={(datum, event) => {
                      event.stopPropagation();
                      selectHolding(datum.data.chartId);
                    }}
                    colors={({ data: item }) => item.color}
                    borderWidth={0}
                    enableArcLabels={false}
                    enableArcLinkLabels={false}
                    tooltip={() => null}
                    theme={{
                      background: "transparent",
                      text: {
                        fontFamily: theme.typography.fontFamily,
                        fill: theme.palette.text.primary,
                      },
                    }}
                  />
                </Box>
                <Box
                  aria-hidden="true"
                  sx={{
                    position: "absolute",
                    inset: "25%",
                    pointerEvents: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    gap: 0.5,
                  }}
                >
                  <Box
                    key={active?.chartId ?? "portfolio-total"}
                    sx={{
                      ...holdingTransitionSx,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {!active && (
                      <>
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                        >
                          Reported value
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: "1.5rem", sm: "1.75rem" },
                            fontWeight: 700,
                            lineHeight: 1.2,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {formatValue(totalValue)}
                        </Typography>
                      </>
                    )}
                    {active && (
                      <>
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                        >
                          Portfolio weight
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.75rem",
                            fontWeight: 700,
                            lineHeight: 1.2,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {formatPercentage(active.value)}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            color: "text.secondary",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {formatValue(active.rawValue)}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
              <Box
                key={active?.chartId ?? "portfolio-total"}
                aria-label="Holding details"
                sx={{
                  ...holdingTransitionSx,
                  minHeight: 48,
                  mt: 1,
                  px: 1,
                  textAlign: "center",
                }}
              >
                {!active && (
                  <Typography
                    sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
                  >
                    Select a holding to explore its portfolio weight.
                  </Typography>
                )}
                {active && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        minHeight: 28,
                      }}
                    >
                      {active.id !== "other" && (
                        <Box sx={{ display: "flex", flexShrink: 0 }}>
                          <Logo
                            src={activeLogoSrc}
                            alt=""
                            ticker={activeSymbol ?? active.label}
                            companyName={active.label}
                            size={24}
                          />
                        </Box>
                      )}
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {active.label}
                      </Typography>
                    </Box>
                    {active.count !== undefined && (
                      <Typography
                        sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
                      >
                        {active.count.toLocaleString()} positions grouped
                        together
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </>
          )}
        </Box>
        {!loading && items.length > 0 && (
          <Box
            sx={{
              minWidth: 0,
              minHeight: { xs: 336, sm: 380 },
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                overflowY: "auto",
                scrollbarGutter: "stable",
                scrollbarWidth: "thin",
                scrollbarColor: `${theme.palette.divider} transparent`,
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  display: "grid",
                  gridTemplateColumns: holdingColumns,
                  gap: 1,
                  px: 1,
                  pb: 1,
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  bgcolor: "background.surface",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Box>Holding</Box>
                <Box
                  sx={{
                    display: { xs: "block", sm: "none" },
                    textAlign: "right",
                  }}
                >
                  Weight / Value
                </Box>
                <Box
                  sx={{
                    display: { xs: "none", sm: "block" },
                    textAlign: "right",
                  }}
                >
                  Weight
                </Box>
                <Box
                  sx={{
                    display: { xs: "none", sm: "block" },
                    textAlign: "right",
                  }}
                >
                  Value
                </Box>
              </Box>
              <Box
                component="ul"
                aria-label="Portfolio holdings"
                sx={{
                  listStyle: "none",
                  m: 0,
                  p: 0,
                }}
              >
                {items.map((item) => {
                  const isActive = active?.chartId === item.chartId;
                  const isSelected = selectedId === item.chartId;
                  const countLabel =
                    item.count === undefined
                      ? ""
                      : `, ${item.count} grouped positions`;
                  return (
                    <Box component="li" key={item.chartId}>
                      <ButtonBase
                        aria-label={`Inspect ${item.label}: ${formatPercentage(item.value)} of portfolio, ${formatValue(item.rawValue)}${countLabel}`}
                        aria-pressed={isSelected}
                        onMouseEnter={() => setHoveredId(item.chartId)}
                        onMouseLeave={() => setHoveredId(null)}
                        onFocus={() => setHoveredId(item.chartId)}
                        onBlur={() => setHoveredId(null)}
                        onClick={() => selectHolding(item.chartId)}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: holdingColumns,
                          alignItems: "center",
                          columnGap: 1,
                          rowGap: 0,
                          width: "100%",
                          minHeight: 44,
                          px: 1,
                          py: { xs: 0.5, sm: 1 },
                          textAlign: "left",
                          borderRadius: 1,
                          color: "text.primary",
                          bgcolor: isActive
                            ? alpha(item.color, 0.08)
                            : "transparent",
                          "&:hover": { bgcolor: "action.hover" },
                          "&:focus-visible": {
                            outline: "2px solid",
                            outlineColor: "primary.main",
                            outlineOffset: -2,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                            gridRow: { xs: "1 / 3", sm: "1" },
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              flexShrink: 0,
                              bgcolor: item.color,
                            }}
                          />
                          <Typography
                            component="span"
                            noWrap
                            title={item.label}
                            sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                          >
                            {item.label}
                          </Typography>
                        </Box>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.8125rem",
                            gridColumn: 2,
                            gridRow: 1,
                            lineHeight: 1.4,
                            color: "text.secondary",
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {formatPercentage(item.value)}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.8125rem",
                            gridColumn: { xs: 2, sm: 3 },
                            gridRow: { xs: 2, sm: 1 },
                            lineHeight: 1.4,
                            fontWeight: 600,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {formatValue(item.rawValue)}
                        </Typography>
                      </ButtonBase>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PortfolioDistributionChart;
